import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { buildIcs } from '@/lib/audition/ics'
import {
  CALL_DESCRIPTION,
  CALL_DURATION_MINUTES,
  callSummary,
  formatSlot,
  PROGRAM_TZ_LABEL,
} from '@/lib/audition/slots'
import type { SubmissionAddress } from '@/lib/audition/submission-address'
import { trpc } from '@/lib/trpc'

/**
 * The applicant's "pick a time" surface (LG-118 Slice 4) — the in-app twin
 * of the timeslot outreach email. Single-select by design: confirming is the
 * booking, so a multi-select would be a promise we can't keep.
 *
 * Renders nothing at all when there is no open proposal, which is the common
 * case — it's safe to mount unconditionally on the done screen.
 *
 * Times are formatted through the same helper the email uses, so the two
 * lists can never disagree about when "2:00 PM ET" is.
 */
export function PickTime({ address }: { address: SubmissionAddress }) {
  const [selected, setSelected] = useState<string | null>(null)
  // Captured at confirm time, because the booked branch below renders after
  // `getOpenProposal` has stopped returning the proposal — confirming flips
  // it out of 'proposed'. Without this the .ics had no id to key on.
  const [confirmed, setConfirmed] = useState<{
    slot: string
    proposalId: string
  } | null>(null)
  const proposal = trpc.audition.getOpenProposal.useQuery(address)
  const confirm = trpc.audition.confirmSlot.useMutation()

  // Checked BEFORE the empty-proposal guard: confirming flips the proposal
  // out of 'proposed', so the next refetch returns null. Guarding first would
  // make the confirmation screen blink out from under the applicant.
  if (confirm.isSuccess && confirmed) {
    return (
      <section className="space-y-3">
        <h3 className="text-lg font-semibold">You're booked 🎉</h3>
        <p className="text-sm text-muted-foreground">
          {`We've got you down for ${formatSlot(confirmed.slot)}. A calendar invite with the meeting link is on its way to your inbox.`}
        </p>
        <Button variant="outline" onClick={() => downloadIcs(confirmed)}>
          Add to calendar (.ics)
        </Button>
      </section>
    )
  }

  if (!proposal.data) return null

  const { proposalId, slots } = proposal.data

  return (
    <section className="space-y-4">
      <h3 className="text-lg font-semibold">Pick a time to chat</h3>
      <p className="text-sm text-muted-foreground">
        Choose whichever works best — all times are shown in {PROGRAM_TZ_LABEL}.
      </p>
      <fieldset>
        {/* The radios' only accessible names are bare timestamps, so the
            group needs its own name. Kept OUTSIDE the spaced stack below:
            `space-y-*` keys off DOM sibling order, and an sr-only legend is
            still a sibling, so it would push the first slot down by 8px. */}
        <legend className="sr-only">Pick a time to chat</legend>
        <div className="space-y-2">
          {slots.map((slot) => (
            <label
              key={slot}
              className="flex cursor-pointer items-center gap-3 rounded-md border p-3 text-sm"
            >
              <input
                type="radio"
                name="call-slot"
                value={slot}
                checked={selected === slot}
                onChange={() => setSelected(slot)}
              />
              {formatSlot(slot)}
            </label>
          ))}
        </div>
      </fieldset>
      {confirm.isError && (
        <p role="alert" className="text-sm text-destructive">
          We couldn't confirm that time — it may have just been taken. Please
          pick another.
        </p>
      )}
      <Button
        disabled={!selected || confirm.isPending}
        onClick={() => {
          if (!selected) return
          setConfirmed({ slot: selected, proposalId })
          confirm.mutate({ ...address, proposalId, slot: selected })
        }}
      >
        Confirm this time
      </Button>
    </section>
  )
}

/**
 * Fallback for applicants who don't use Google Calendar. The meeting link is
 * created asynchronously, so it is deliberately absent here — the emailed
 * invite is the one that carries it.
 */
function downloadIcs({
  slot,
  proposalId,
}: {
  slot: string
  proposalId: string
}) {
  const ics = buildIcs({
    start: new Date(slot),
    durationMinutes: CALL_DURATION_MINUTES,
    summary: callSummary(),
    description: CALL_DESCRIPTION,
    // The proposal id, per buildIcs's contract. Keying on the slot time
    // instead made the UID identical for every applicant who picked the same
    // time, and calendar clients treat UID as event identity — so one
    // person's download could overwrite or dedupe against another's.
    uid: `${proposalId}@beyondctci.com`,
    stamp: new Date(),
  })
  const url = URL.createObjectURL(
    new Blob([ics], { type: 'text/calendar;charset=utf-8' }),
  )
  const link = document.createElement('a')
  link.href = url
  link.download = 'beyond-ctci-intro-call.ics'
  link.click()
  // Deferred: revoking synchronously can beat the download commit in
  // Firefox and Safari, and this .ics IS the fallback for people who don't
  // use Google Calendar — exactly the ones who'd get nothing.
  setTimeout(() => URL.revokeObjectURL(url), 0)
}
